import cc from 'classcat';
import React, { useState, useEffect } from 'react';

type ButtonVariant = 'contained' | 'text' | 'bordered' | 'underline' | 'danger';
type ButtonColor = 'primary' | 'success' | 'error' | 'gray';

interface IButtonWithDropDown {
    className?: string;
    variant?: ButtonVariant;
    color?: ButtonColor;
    disabled?: boolean;
    testId?: string;
    options?: { label: string; onClick: () => void }[];
    defaultSelected?: { label: string; onClick: () => any };
    dropDirection?: 'up' | 'down';
    type?: 'submit' | 'reset' | 'button';
}

export const ButtonWithDropDown = ({
    className,
    type,
    disabled,
    variant = 'contained',
    color = 'primary',
    testId,
    options,
    defaultSelected,
    dropDirection = 'down',
}: IButtonWithDropDown) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<{ label: string; onClick: () => void } | undefined>(defaultSelected);

    const handleButtonClick = () => {
        if (!disabled && defaultSelected) {
            defaultSelected.onClick();
            setIsOpen(false);
        }
    };

    const toggleDropdown = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (!disabled) setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleOutsideClick = () => setIsOpen(false);
        if (isOpen) document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [isOpen]);

    return (
        <div className={cc(['button-dropdown', `button-dropdown--${dropDirection}`])}>
            <button
                type={type || 'button'}
                onClick={handleButtonClick}
                disabled={disabled}
                className={cc([
                    'button-style',
                    `button-style--${variant}`,
                    `button-style--${variant}--${color}`,
                    {
                        'button-style--with-icon': options,
                        'button-style--dropdown-open': isOpen,
                    },
                    className,
                ])}
                data-testid={testId}
            >
                {defaultSelected?.label || options?.[0].label}

                <span className="button-icon-right" onClick={toggleDropdown}>
                    {isOpen ? (
                        <img src="/svg/sidebar/arrow-up.svg" width={14} height={14} alt="arrow up" />
                    ) : (
                        <img src="/svg/sidebar/arrow-down.svg" width={14} height={14} alt="arrow down" />
                    )}
                </span>
            </button>

            {isOpen && options && (
                <ul className="button-dropdown-menu">
                    {options.map((option, index) => (
                        <li
                            key={index}
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsOpen(false);
                                option.onClick();
                            }}
                            className={cc(['button-dropdown-option', { 'button-dropdown-option--selected': option === selectedOption }])}
                        >
                            <span>{option.label}</span>
                            {option.label === selectedOption?.label && (
                                <svg
                                    className="check-icon"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
